import { Component, Input, Output, EventEmitter, signal, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, firstValueFrom } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { ValidationService } from "../../service/validation.service";
import { Account } from "../../entity/Account";
import { State } from "../../entity/State";
import { AccountNumberIssueDialog } from "./account-number-issue-dialog.component";
import { Company } from "../../entity/Company";
import { User } from "../../entity/User";
import { AbstractBuyerComponent } from "../abstract/buyer.component";
import { AuthService } from "../../service/auth.service";
import { NgxMaskDirective } from "ngx-mask";

@Component({
    selector: "kilowant-account-edit",
    templateUrl: "../../../html/incs/account-edit.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgxMaskDirective, ReactiveFormsModule]
})
export class AccountEditComponent extends AbstractBuyerComponent {
    @Output() finished = new EventEmitter<boolean>();
    @Output() cancelled = new EventEmitter<boolean>();

    account: Account;
    accountBackup: Account | null = null;
    billsExpired: boolean;
    currentAccountNumber: FormControl;
    userCompanies: Company[];

    clientType: string = "";

    @Output("accountChange") accountEmitter: EventEmitter<Account> = new EventEmitter<Account>();
    @Input("account") set setAccountValue(value: Account) {
        this.account = value;
    }

    @Output("currentAccountNumberChange") currentAccountNumberEmitter: EventEmitter<FormControl> = new EventEmitter<FormControl>();
    @Input("currentAccountNumber") set setCurrentAccountNumber(value: FormControl) {
        this.currentAccountNumber = value;
    }

    @Input("isFromOffer") isFromOffer: boolean = false;

    @Input("disabledDelete") disabledDelete: boolean = false;

    @Output("changed") changedEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input() callUpdate: Subject<boolean>;
    @Input() callCancel: Subject<boolean>;

    cities: string[] = [];
    toBeUsedUser: User;

    fileToUpload: File | null = null;
    deleteTaxExemptCertificateDocument: boolean;
    newCompanyRegistration = signal<boolean>(false);
    taxExemptAllowToAdd = signal<boolean>(true);
    taxExempt: boolean = false;

    constructor(
        router: Router,
        route: ActivatedRoute,
        analyticsService: AnalyticsService,
        authService: AuthService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
    }

    ngOnInit(): void {
        this.eventSubscriptions.push(
            this.callUpdate.subscribe((v: any) => {
                this.updateAccount();
            })
        );
        this.eventSubscriptions.push(
            this.callCancel.subscribe((v: any) => {
                this.cancel();
            })
        );
        super.ngOnInit();
    }

    pageStart(): void {
        if (this.user.isAdvisor) {
            let client = this.generalService.getStoredClient();
            if (client != null) {
                this.toBeUsedUser = client;
            } else {
                this.toBeUsedUser = this.user;
            }
        } else {
            this.toBeUsedUser = this.user;
        }

        this.accountBackup = Object.assign(new Account(), this.account);
        this.accountBackup.billImages = this.account.billImages;
        this.currentAccountNumber.setValue(this.account.accountNumber);
        if (this.account != null) {
            this.filterCities(this.account.state);
        }
        this.clientType = (this.account.company == null) ? 'Residential' : 'Commercial';
        firstValueFrom(this.generalService.getUsersCompanies()).then((companies: any) => {
            this.userCompanies = [];
            for (let i = 0; i < companies.length; i++) {
                if (companies[i] != null) {
                    this.userCompanies.push(companies[i]);
                }
            }
            if (this.clientType == "Commercial" &&  this.userCompanies.length == 0) {
                this.setNewCompany();
            }
            this.refreshView();
        });

        this.taxExempt = false;
        this.selectExistingCompany();
    }

    ngAfterViewInit() {
        if (this.account == null || this.account.utility == null) {
            this.cancel();
            return;
        }
    }

    filterCities(state: State | null): void {
        this.cities = [];
        if (state == null) {
            return;
        }
        this.generalService.getCities(state).then((cities) => {
            this.cities = cities;
            this.refreshView();
        });
    }

    mapAccountNumber(): void {
        this.account.accountNumber = this.currentAccountNumber.value;
    }

    updateAccount(): void {
        if (this.account.keyName == null || this.account.keyName == "") {
            this.addError("keyName");
        }
        if (this.account.serviceName == null || this.account.serviceName == "") {
            this.addError("serviceName");
        }
        if (this.account.addressLine1 == null || this.account.addressLine1 == "") {
            this.addError("addressLine1");
        }
        if (this.account.zip == null || this.account.zip == "") {
            this.addError("zip");
        }
        if (this.currentAccountNumber.value == null || this.currentAccountNumber.value.length == 0) {
            this.addError("accountNumber");
        }
        if (this.clientType=="Residential") {
            this.account.company = null;
        }
        if (this.clientType == "Commercial") {
            if (this.account.company != null && this.account.company.taxExempt == true && (this.account.company.federalTaxID == null || this.account.company.federalTaxID.length == 0)) {
                this.addError("FederalTaxID");
            }
            if (this.account.company != null && this.account.company.taxExempt == true && this.fileToUpload == null && this.account.company.taxExemptCertificateDocument == null) {
                this.addError("fileToUpload");
            }
            if (this.account.company == null) {
                this.addError("selectCompany");
            }
        }
        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above in order to update your account.");
            return;
        }
        if (this.account.city == null || this.account.city.length == 0 || this.account.state == null) {
            this.addError("zip");
            this.setErrorText("Zip code is not correct.");
            return;
        }
        if (!this.validationService.validate("Account", "serviceName", this.account.serviceName, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "addressLine1", this.account.addressLine1, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "city", this.account.city, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "zip", this.account.zip, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "keyName", this.account.keyName, this.errorY)) {
            return;
        }
        if (this.account.company != null && (this.account.company.name == null || this.account.company.name.length == 0)) {
            this.addError("companyName");
            this.setErrorText("Company name can not be empty.");
            return;
        }
        if (!this.currentAccountNumber.valid) {
            this.addError("accountNumber");
            this.setErrorText("Account number is not correct.");
            return;
        }

        this.generalService.ifAccountNumberUsed(this.account.utility!, this.currentAccountNumber.value, this.account.id).then(res => {
            if (res.value) {
                this.addError("accountNumber");
                this.setErrorText("Account with such number already exists.");
                return;
            } else if (res.key) {
                this.addError("accountNumber");
                this.setErrorText("accountissue");
                return;
            } else {
                this.account.accountNumber = this.currentAccountNumber.value;
                this.generalService.updateAccount(this.account, [], [], this.fileToUpload).subscribe((res: any) => {
                    this.generalService.clearUserCompaniesFromSession();
                    this.finished.next(true);
                },
                    err => {
                        if (err.status == 400) {
                            this.setErrorText(err.error);
                        }
                    });
            }
        });
    }

    resetErrorPlusEvent(field: string): void {
        this.resetError(field);
        this.changedEmitter.next(true);
    }

    hasHighlightedFields(): boolean {
        if (this.isFromOffer && (this.currentAccountNumber.value == null || this.currentAccountNumber.value.length == 0)) {
            return true;
        }
        if (this.isFromOffer && (this.account.serviceName == null || this.account.serviceName.length == 0)) {
            return true;
        }
        if (this.isFromOffer && (this.account.addressLine1 == null || this.account.addressLine1.length == 0)) {
            return true;
        }
        if (this.isFromOffer && (this.account.zip == null || this.account.zip.length == 0)) {
            return true;
        }
        return false;
    }

    cancel(): void {
        if (this.accountBackup != null) {
            this.account = Object.assign(this.account, this.accountBackup);
            this.account.billImages = this.accountBackup.billImages;
        }
        this.clearErrors();
        this.cancelled.next(true);
    }

    reportAccountIssue(): void {
        var dialogRef = this.dialog.open(AccountNumberIssueDialog, {
            disableClose: false,
            panelClass: "card-popup"
        });
        dialogRef.componentInstance.info = "Please, provide more details about your account (optional).";
        dialogRef.componentInstance.message = "My Account Number: " + this.currentAccountNumber.value + " has been used without my authorization. Please, investigate this.";
        dialogRef.componentInstance.utility = this.account.utility;
        dialogRef.componentInstance.accountNumber = this.currentAccountNumber.value;

        dialogRef.afterClosed().subscribe((result: any) => {
            dialogRef = null!;
        });
    }

    getStateName(state: State | null): string {
        if (state == null) {
            return "";
        }
        return state.stateCode;
    }

    zipChanged(): void {
        this.resetError('zip');
        this.account.city = "";
        this.account.state = null;
        if (this.account.zip != null && this.account.zip?.length > 2) {
            let request = this.generalService.getStateCityByZip(this.account.zip);
            if (request == null) {
                return;
            }
            firstValueFrom(request).then(data => {
                if (data != null) {
                    this.account.state = data.key;
                    this.account.city = data.value;
                    this.refreshView();
                }
            });
        }
    }


    handleFileInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.fileToUpload = (target.files as FileList).item(0) as File;
        this.resetError("fileToUpload");
        setTimeout(() => { this.refreshView(); }, 100);
        
    }
    deleteFile() {
        this.fileToUpload = null;
        this.refreshView();
    }
    deleteExistingFile(key: number) {
        this.deleteTaxExemptCertificateDocument = true;
        this.account!.company!.taxExemptCertificateDocument = null!;
        this.refreshView();
    }
    downloadFile(ifExisting: boolean, id: number | null) {
        if (!ifExisting) {
            this.generalService.sendFileToClient(this.fileToUpload);
        } else if (id != null) {
            this.generalService.openDocument(id);
        }
    }

    taxExemptChanged(): void {
        if (this.newCompanyRegistration() || this.taxExemptAllowToAdd()) {
            this.account!.company!.taxExempt = this.taxExempt;
            this.refreshView();
            return;
        }
        if (!this.taxExempt) {
            this.account.company = new Company();
            this.account.company.name = this.toBeUsedUser.company!.name;
            this.account.company.taxExempt = false;
        } else {
            this.account.company = this.toBeUsedUser.company;
            this.account.company!.taxExempt = true;
        }
        this.refreshView();
    }

    setNewCompany(): void {
        this.taxExempt = false;
        this.account.company = new Company();
        this.taxExempt = false;
        this.clearErrors();
        this.newCompanyRegistration.set(true);
    }

    selectExistingCompany(): void {
        this.taxExempt = this.account.company?.taxExempt!;
        this.taxExemptAllowToAdd.set(true);
        this.clearErrors();
        this.refreshView();
    }

    switchToCompanySelect(): void {
        this.account.company = null;
        this.newCompanyRegistration.set(false);
        this.clearErrors();
    }

    switchClientType(): void {
        if (this.clientType == "Commercial" && this.user!.company == null) {
            if (this.account.company == null) {
                this.account.company = new Company();
            }
        }
        if (this.clientType == "Residential") {
            this.account.company = null;
        }
        this.clearErrors();
        this.refreshView();
    }

}
